/**
 * The optional online Daily Dig board (docs/spec/06 §7). The game never depends on it: every call
 * resolves to null or false when there is no server, and nothing waits on it for long.
 */

export interface BoardEntry {
  readonly initials: string;
  readonly score: number;
  readonly seconds: number;
}

/** Long enough for a stopped machine to wake: the host starts it on the first request. */
const TIMEOUT_MS = 12000;

async function call(url: string, init?: RequestInit): Promise<Response | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...init, signal: ctrl.signal });
    return res.ok ? res : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Today's board, best first; null when there is no server. */
export async function fetchDailyBoard(day: number): Promise<BoardEntry[] | null> {
  const res = await call(`api/daily?day=${day}`);
  if (!res) return null;
  try {
    const body = (await res.json()) as { entries?: unknown };
    if (!Array.isArray(body.entries)) return null;
    return body.entries
      .filter((e): e is BoardEntry => typeof e === 'object' && e !== null && typeof (e as BoardEntry).score === 'number')
      .map((e) => ({ initials: String(e.initials).slice(0, 3), score: e.score, seconds: e.seconds }));
  } catch {
    return null;
  }
}

/**
 * Post a finished run with its log (game/gameLog), which the server replays to check the score and
 * time; true if the server took it.
 */
export async function submitDaily(day: number, entry: BoardEntry, proof: string): Promise<boolean> {
  const res = await call('api/daily', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ day, ...entry, proof }),
  });
  return res !== null;
}
